import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import notificationsService from '../services/notifications';
import { inductionNotificationPath } from '../utils/inductionNotifications';
import { tilNotificationPath } from '../utils/tilNotifications';

/**
 * Registers the device for FCM / APNs push notifications and wires up
 * foreground + tap handlers.
 *
 * Must be used inside a component that has access to React Router context
 * (i.e. inside <Router>).
 *
 * On web this is a no-op — push notifications only run on native builds.
 */
export function usePushNotifications() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Only run on native Capacitor platforms (Android / iOS)
    if (!Capacitor.isNativePlatform()) return;
    if (!isAuthenticated) return;

    let cleanup: (() => void) | undefined;

    async function setup() {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        // Create a notification channel required for Android 8+ (Oreo and above)
        if (Capacitor.getPlatform() === 'android') {
          await PushNotifications.createChannel({
            id: 'default',
            name: 'Default',
            description: 'Default notification channel',
            importance: 5, // IMPORTANCE_HIGH — shows as heads-up notification
            visibility: 1, // VISIBILITY_PUBLIC
            vibration: true,
            sound: 'default',
          });
        }

        // Request permission
        const permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive !== 'granted') {
          console.warn('Push notification permission not granted');
          return;
        }

        // Register with FCM / APNs
        await PushNotifications.register();

        // Receive the device token and send it to the backend
        const tokenListener = await PushNotifications.addListener(
          'registration',
          async (tokenData) => {
            const platform = Capacitor.getPlatform() as 'ios' | 'android';
            try {
              await notificationsService.registerPushToken(tokenData.value, platform);
            } catch (err) {
              console.error('Failed to register push token with server:', err);
            }
          }
        );

        // Log registration errors
        const registrationErrorListener = await PushNotifications.addListener(
          'registrationError',
          (err) => {
            console.error('Push registration error:', err);
          }
        );

        // Foreground (and background data-only) push handler.
        const foregroundListener = await PushNotifications.addListener(
          'pushNotificationReceived',
          async () => {
            // Refresh the unread notification badge.
            window.dispatchEvent(new CustomEvent('push-notification-received'));
          }
        );

        // Handle push notification tap (background / killed app)
        const actionListener = await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (action) => {
            const data = action.notification.data as Record<string, string> | undefined;
            const type = data?.type;
            if (type === 'timesheet_submitted' || type === 'leave_submitted' || type === 'missed_clock_declared' || type === 'timesheet_approval_reminder') {
              navigate('/approvals');
            } else if (type === 'timesheet_approved' || type === 'timesheet_rejected') {
              navigate('/timesheet/history');
            } else if (type === 'timesheet_reminder' || type === 'timesheet_submit_nudge') {
              navigate('/timesheet');
            } else if (type === 'leave_approved' || type === 'leave_rejected') {
              navigate('/leave');
            } else if (inductionNotificationPath(type, data)) {
              navigate(inductionNotificationPath(type, data)!);
            } else if (tilNotificationPath(type, data)) {
              navigate(tilNotificationPath(type, data)!);
            } else if (type?.startsWith('equipment_insurance_') && data?.policy_id) {
              // Insurance due or lapsed — the policy, which names its machines.
              navigate(`/equipment/insurance/${data.policy_id}`);
            } else if (type?.startsWith('equipment_') && data?.equipment_id) {
              // Service due, loans overdue, rego expiring — straight to the machine.
              navigate(`/equipment/${data.equipment_id}`);
            } else if (type === 'po_receipt_added' && data?.po_id) {
              navigate(`/purchase-orders/${data.po_id}`);
            } else if (type?.startsWith('spray_weather_') && data?.spray_id) {
              navigate(`/spray/${data.spray_id}`);
            } else if (type === 'pest_alert') {
              const q = new URLSearchParams();
              if (data?.farm_id) q.set('farm', data.farm_id);
              if (data?.pest_model_id) q.set('pest', data.pest_model_id);
              const qs = q.toString();
              navigate(`/weather/pests${qs ? `?${qs}` : ''}`);
            } else {
              navigate('/dashboard');
            }
          }
        );

        // Handle local notification tap (scheduled reminders)
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        type ActionPerformed = import('@capacitor/local-notifications').ActionPerformed;
        const localActionListener = await LocalNotifications.addListener(
          'localNotificationActionPerformed',
          (action: ActionPerformed) => {
            const extra = action.notification.extra as Record<string, string> | undefined;
            if (extra?.type === 'timesheet_reminder') {
              navigate('/timesheet');
            }
          }
        );

        cleanup = () => {
          tokenListener.remove();
          registrationErrorListener.remove();
          foregroundListener.remove();
          actionListener.remove();
          localActionListener.remove();
        };
      } catch (err) {
        console.error('Push notification setup error:', err);
      }
    }

    setup();

    return () => {
      if (cleanup) cleanup();
    };
  }, [isAuthenticated, navigate]);
}
